/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';

import { DeleteOutlined, MenuOutlined, VerticalAlignTopOutlined } from '@ant-design/icons';
import { DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Modal, Select, Table, Typography, message } from 'antd';
import { ColumnsType } from 'antd/lib/table';

import { useAppDispatch, useAppSelector } from '@/Hooks/useAppRedux';
import { ChannelData } from '@/Interfaces/I_vod';
import { PostManagementData } from '@/Interfaces/I_postManagement';
import {
  deleteChannelPost,
  postChannelPost,
  resequenceChannelPosts,
} from '@/Redux/slices/VodSlice';
import { fetchVodAPI } from '@/Axios/api/postManagement';

import type { DragEndEvent } from '@dnd-kit/core';

const MAX_PINNED = 20;

interface PostRow {
  key: string;
  postUuid: string;
  title: string;
}

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string;
}

const Row = ({ children, ...props }: RowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props['data-row-key'] });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Transform.toString(transform && { ...transform, scaleY: 1 }),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  return (
    <tr {...props} ref={setNodeRef} style={style} {...attributes}>
      {React.Children.map(children, (child) => {
        if ((child as React.ReactElement).key === 'sort') {
          return React.cloneElement(child as React.ReactElement<any>, {
            children: (
              <MenuOutlined
                ref={setActivatorNodeRef}
                style={{ touchAction: 'none', cursor: 'move' }}
                {...listeners}
              />
            ),
          });
        }
        return child;
      })}
    </tr>
  );
};

interface Props {
  channel: ChannelData | null;
  open: boolean;
  onClose: () => void;
}

const ChannelPostResequenceModal = ({ channel, open, onClose }: Props) => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state) => state.vod.loading);

  const [rows, setRows] = useState<PostRow[]>([]);
  const [originalRows, setOriginalRows] = useState<PostRow[]>([]);
  const [pristine, setPristine] = useState(true);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [channelPosts, setChannelPosts] = useState<PostManagementData[]>([]);
  const [unpinnedPosts, setUnpinnedPosts] = useState<PostManagementData[]>([]);

  useEffect(() => {
    if (!open || !channel) {
      setChannelPosts([]);
      setRows([]);
      setOriginalRows([]);
      setUnpinnedPosts([]);
      setPristine(true);
      return;
    }

    const load = async () => {
      setFetchLoading(true);
      try {
        const [pinnedRes, unpinnedRes] = await Promise.all([
          fetchVodAPI({ channel_id: channel.channelId, page_size: MAX_PINNED, filter: 'PINNED' }),
          fetchVodAPI({ channel_id: channel.channelId, page_size: 200, filter: 'UNPINNED' }),
        ]);

        const posts: PostManagementData[] = pinnedRes.data.data ?? [];
        setChannelPosts(posts);
        const built = posts.map((post) => ({
          key: post.uuid,
          postUuid: post.uuid,
          title: post.title,
        }));
        setRows(built);
        setOriginalRows(built);
        setPristine(true);

        setUnpinnedPosts(unpinnedRes.data.data ?? []);
      } catch {
        message.error({
          content: 'Failed to load channel posts.',
          duration: 3,
          style: { marginTop: '120px' },
        });
      } finally {
        setFetchLoading(false);
      }
    };

    load();
  }, [open, channel]);

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setRows((prev) => {
        const activeIndex = prev.findIndex((r) => r.key === active.id);
        const overIndex = prev.findIndex((r) => r.key === over?.id);
        return arrayMove(prev, activeIndex, overIndex);
      });
      setPristine(false);
    }
  };

  const onMoveToTop = (row: PostRow) => {
    setRows((prev) => {
      const index = prev.findIndex((r) => r.key === row.key);
      if (index <= 0) return prev;
      const next = [...prev];
      next.splice(index, 1);
      next.unshift(row);
      return next;
    });
    setPristine(false);
  };

  const onReset = () => {
    setRows(originalRows);
    setPristine(true);
  };

  const onSave = () => {
    if (!channel) return;
    dispatch(
      resequenceChannelPosts({
        channelId: channel.channelId,
        postOrder: rows.map((r) => r.postUuid),
      }),
    );
    setPristine(true);
    onClose();
  };

  const onRemove = (row: PostRow) => {
    if (!channel) return;
    dispatch(deleteChannelPost({ channelId: channel.channelId, postUuid: [row.postUuid] }));
    const removed = channelPosts.find((p) => p.uuid === row.postUuid);
    if (removed) setUnpinnedPosts((prev) => [...prev, removed]);
    setRows((prev) => prev.filter((r) => r.key !== row.key));
    setOriginalRows((prev) => prev.filter((r) => r.key !== row.key));
    setChannelPosts((prev) => prev.filter((p) => p.uuid !== row.postUuid));
  };

  const unpinnedOptions = unpinnedPosts.map((p) => ({ value: p.uuid, label: p.uuid }));

  const onAddPost = (uuid: string) => {
    if (!channel) return;
    if (rows.length >= MAX_PINNED) {
      message.error({
        content: `Cannot add more than ${MAX_PINNED} pinned posts.`,
        duration: 3,
        style: { marginTop: '120px' },
      });
      return;
    }
    const post = unpinnedPosts.find((p) => p.uuid === uuid);
    if (!post) return;
    dispatch(postChannelPost({ channelId: channel.channelId, postUuid: [uuid] }));
    const newRow = { key: uuid, postUuid: uuid, title: post.title };
    setRows((prev) => [newRow, ...prev]);
    setOriginalRows((prev) => [newRow, ...prev]);
    setUnpinnedPosts((prev) => prev.filter((p) => p.uuid !== uuid));
    setPristine(false);
  };

  const columns: ColumnsType<PostRow> = [
    {
      key: 'moveToTop',
      width: 40,
      render: (_, row) => (
        <Button
          type="text"
          icon={<VerticalAlignTopOutlined />}
          disabled={loading}
          onClick={() => onMoveToTop(row)}
        />
      ),
    },
    { key: 'sort', width: 40 },
    {
      title: 'Title',
      dataIndex: 'title',
      render: (title: string) => <Typography.Text>{title}</Typography.Text>,
    },
    {
      title: 'Post UUID',
      dataIndex: 'postUuid',
      render: (uuid: string) => <Typography.Text>{uuid}</Typography.Text>,
    },
    {
      title: 'Action',
      key: 'action',
      width: 50,
      render: (_, row) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          disabled={loading}
          onClick={() => onRemove(row)}
        />
      ),
    },
  ];

  return (
    <Modal
      title={`Manage Pinned Posts — [${channel?.channelId}] ${channel?.channelName ?? ''}`}
      open={open}
      onCancel={onClose}
      width={700}
      footer={
        <div className="flex justify-between">
          <div className="flex gap-2">
            <Select
              showSearch
              placeholder="Add post by UUID"
              style={{ width: 320 }}
              options={unpinnedOptions}
              onChange={onAddPost}
              value={null}
              disabled={loading || fetchLoading || rows.length >= MAX_PINNED}
              filterOption={(input, option) =>
                String(option?.value ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={onReset} disabled={pristine || loading}>
              Reset
            </Button>
            <Button type="primary" onClick={onSave} disabled={pristine || loading}>
              Save
            </Button>
          </div>
        </div>
      }
    >
      <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
        <SortableContext items={rows.map((r) => r.key)} strategy={verticalListSortingStrategy}>
          <Table
            components={{ body: { row: Row } }}
            rowKey="key"
            columns={columns}
            dataSource={rows}
            pagination={false}
            loading={loading || fetchLoading}
            size="small"
          />
        </SortableContext>
      </DndContext>
    </Modal>
  );
};

export default ChannelPostResequenceModal;
